// -----------------------------------------------------------------------------
// Manages the settings for the running application
// -----------------------------------------------------------------------------
import objectUtils from '../utils/object-utils'

const activeTz = process.env.VUE_APP_TIME_ZONE || 'America/Phoenix'

export default {
  namespaced: true,

  state: {
    // Maintains the active running app meta
    name: '',
    alias: '',
    version: '0.0.0',
    platform: '',

    // Application settings for the active web version
    // (These values will be loaded on startup, and saved to local storage)
    settings: (() => {
      const savedSettings = JSON.parse(localStorage.getItem('settings') || '{}')
      return Object.assign(
        {
          // default values
          locale: 'en-us',
          timezone: activeTz,
          enableFaCardScanner: false
        },
        savedSettings
      )
    })(),

    // The active root page (for navigation management)
    activeRootPage: null,

    // ------------------------------------------------------- Download requests

    // downloadStatus: {
    //   order_invoices: {},
    //   order_attachments: {},
    //   route_manifests: {}
    // },
    downloadStatus: {},

    // ---------------------------------------------------------------- Datasets

    datasetUsStates: [
      { code: 'AL', label: 'Alabama' },
      { code: 'AK', label: 'Alaska' },
      { code: 'AZ', label: 'Arizona' },
      { code: 'AR', label: 'Arkansas' },
      { code: 'CA', label: 'California' },
      { code: 'CO', label: 'Colorado' },
      { code: 'CT', label: 'Connecticut' },
      { code: 'DE', label: 'Delaware' },
      { code: 'DC', label: 'District Of Columbia' },
      { code: 'FL', label: 'Florida' },
      { code: 'GA', label: 'Georgia' },
      { code: 'HI', label: 'Hawaii' },
      { code: 'ID', label: 'Idaho' },
      { code: 'IL', label: 'Illinois' },
      { code: 'IN', label: 'Indiana' },
      { code: 'IA', label: 'Iowa' },
      { code: 'KS', label: 'Kansas' },
      { code: 'KY', label: 'Kentucky' },
      { code: 'LA', label: 'Louisiana' },
      { code: 'ME', label: 'Maine' },
      { code: 'MD', label: 'Maryland' },
      { code: 'MA', label: 'Massachusetts' },
      { code: 'MI', label: 'Michigan' },
      { code: 'MN', label: 'Minnesota' },
      { code: 'MS', label: 'Mississippi' },
      { code: 'MO', label: 'Missouri' },
      { code: 'MT', label: 'Montana' },
      { code: 'NE', label: 'Nebraska' },
      { code: 'NV', label: 'Nevada' },
      { code: 'NH', label: 'New Hampshire' },
      { code: 'NJ', label: 'New Jersey' },
      { code: 'NM', label: 'New Mexico' },
      { code: 'NY', label: 'New York' },
      { code: 'NC', label: 'North Carolina' },
      { code: 'ND', label: 'North Dakota' },
      { code: 'OH', label: 'Ohio' },
      { code: 'OK', label: 'Oklahoma' },
      { code: 'OR', label: 'Oregon' },
      { code: 'PA', label: 'Pennsylvania' },
      { code: 'RI', label: 'Rhode Island' },
      { code: 'SC', label: 'South Carolina' },
      { code: 'SD', label: 'South Dakota' },
      { code: 'TN', label: 'Tennessee' },
      { code: 'TX', label: 'Texas' },
      { code: 'UT', label: 'Utah' },
      { code: 'VT', label: 'Vermont' },
      { code: 'VA', label: 'Virginia' },
      { code: 'WA', label: 'Washington' },
      { code: 'WV', label: 'West Virginia' },
      { code: 'WI', label: 'Wisconsin' },
      { code: 'WY', label: 'Wyoming' }
    ]
  }, // END - state

  getters: {
    // ----------------------------------------------------------- App meta info
    name (state) {
      return state.name
    },

    alias (state) {
      return state.alias
    },

    version (state) {
      return state.version
    },

    platform (state) {
      return state.platform
    },

    // ------------------------------------------------------ Persisted settings

    settings (state) {
      return state.settings
    },

    defaultTimezone (state) {
      return state.settings.timezone
    },

    enableFaCardScanner (state) {
      return state.settings.enableFaCardScanner
    },

    // ----------------------------------------------------- Navigation handling

    activeRootPage (state) {
      return state.activeRootPage
    },

    // ---------------------------------------------------------------- Datasets

    datasetUsStates (state) {
      return state.datasetUsStates
    }
  }, // END - getters

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  //
  // The "context" parameter is implicitly injected into every
  // "store.dispatch()" invocation made by the components. Use the functions
  // and data exposed by the context object, in order to implement the
  // action logic.
  //
  // The context object will expose:
  //
  // context: {
  //   commit(),        [use this function to invoke a mutation]
  //   dispatch(),      [use this function to invoke an action]
  //   getters,         [use this function to invoke a getter]
  //   rootState,       [access to the full state]
  //   state,           [access to the local state of this module]
  // }
  // ---------------------------------------------------------------------------
  actions: {

    setName (context, name) {
      context.commit('setAppName', name)
    },

    setAlias (context, alias) {
      context.commit('setAppAlias', alias)
    },

    setVersion (context, version) {
      context.commit('setAppVersion', version)
    },

    setPlatform (context, platform) {
      context.commit('setAppPlatform', platform)
    }

  }, // END - actions

  mutations: {
    // ----------------------------------------------------- App meta management
    setAppName (state, name) {
      state.name = name
    },

    setAppAlias (state, alias) {
      state.alias = alias
    },

    setAppVersion (state, version) {
      state.version = version
    },

    setAppPlatform (state, platform) {
      state.platform = platform
    },

    // ------------------------------------------------ User settings management

    setEnableFaCardScanner (state, value) {
      state.settings = {
        ...state.settings,
        enableFaCardScanner: value === true
      }

      localStorage.setItem('settings', JSON.stringify(state.settings))
    },

    // --------------------------------------------- Download request management

    setOrderInvoicesToDownload (state, orders = []) {
      for (const order of orders) {
        const itemType = (order.is_credit) ? 'cinvoice' : 'invoice'
        const itemKey = `${order.order_number}_${itemType}`
        this._vm.$set(state.downloadStatus, itemKey, 'downloading')
      }
    },

    setOrderInvoiceDownloadComplete (state, order = {}) {
      const itemType = (order.is_credit) ? 'cinvoice' : 'invoice'
      const itemKey = `${order.order_number}_${itemType}`
      this._vm.$set(state.downloadStatus, itemKey, 'completed')
    },

    setOrderInvoiceDownloadError (state, order = {}) {
      const itemType = (order.is_credit) ? 'cinvoice' : 'invoice'
      const itemKey = `${order.order_number}_${itemType}`
      this._vm.$set(state.downloadStatus, itemKey, 'error')
    },

    setOrderAttachmentsToDownload (state, order = {}) {
      const attachments = order.attachment_docs
      const itemType = (order.is_credit) ? 'cattachment' : 'attachment'
      if (!attachments || objectUtils.isEmpty(attachments)) return false

      Object.keys(attachments).forEach(attachmentKey => {
        const itemKey = `${order.order_number}_${itemType}_${attachments[attachmentKey].order}`
        this._vm.$set(state.downloadStatus, itemKey, 'downloading')
      })
    },

    setOrderAttachmentDownloadComplete (state, dataOpts = {}) {
      const order = dataOpts.order
      const attachmentInfo = dataOpts.attachmentInfo
      const itemType = (order.is_credit) ? 'cattachment' : 'attachment'
      const itemKey = `${order.order_number}_${itemType}_${attachmentInfo.order}`
      this._vm.$set(state.downloadStatus, itemKey, 'completed')
    },

    setOrderAttachmentDownloadError (state, dataOpts = {}) {
      const order = dataOpts.order
      const attachmentInfo = dataOpts.attachmentInfo
      const itemType = (order.is_credit) ? 'cattachment' : 'attachment'
      const itemKey = `${order.order_number}_${itemType}_${attachmentInfo.order}`
      this._vm.$set(state.downloadStatus, itemKey, 'error')
    },

    clearCompletedDownloads (state) {
      Object.keys(state.downloadStatus).forEach(itemKey => {
        if (state.downloadStatus[itemKey] === 'completed') {
          this._vm.$delete(state.downloadStatus, itemKey)
        }
      })
    }
  } // END - mutations
}
